import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp, 
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, TrendingUp, TrendingDown, Wallet,
  CreditCard, Banknote, Download, RefreshCw
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { transporterAPI } from '@/services/api';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Bar, BarChart } from 'recharts';

const TransporterEarnings = () => {
  const { stats } = useTransporterStats();
  const [payments, setPayments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    start_date: '',
    end_date: '',
  });
  
  const sidebarLinks = getTransporterSidebarLinks(stats);

  // Charger les données des paiements
  React.useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const [paymentsResponse, monthlyResponse] = await Promise.all([
          transporterAPI.getPayments(filters),
          transporterAPI.getMonthlyBreakdown(12)
        ]);
        
        setPayments(paymentsResponse.data.results || paymentsResponse.data);
        setMonthlyData(monthlyResponse.data);
      } catch (err) {
        console.error('Erreur lors du chargement des paiements:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [filters]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'En attente', color: 'bg-yellow-500' },
      'SUCCESS': { label: 'Réussi', color: 'bg-green-500' },
      'FAILED': { label: 'Échoué', color: 'bg-red-500' },
      'REFUNDED': { label: 'Remboursé', color: 'bg-blue-500' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getPaymentMethodIcon = (method) => {
    const iconConfig = {
      'ORANGE_MONEY': CreditCard,
      'MTN_MOMO': CreditCard,
      'BANK_TRANSFER': Banknote,
      'CARD': CreditCard,
      'PLATFORM_BALANCE': Wallet,
    };
    
    const Icon = iconConfig[method] || CreditCard;
    return <Icon className="w-4 h-4" />;
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M GNF`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K GNF`;
    }
    return `${amount} GNF`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleExportPayments = () => {
    toast({
      title: 'Export en cours',
      description: 'La fonctionnalité d\'export sera bientôt disponible.',
    });
  };

  const handleRefreshData = () => {
    setLoading(true);
    // Recharger les données
    window.location.reload();
  };

  return (
    <>
      <Helmet>
        <title>Revenus et Paiements - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Revenus et Paiements</h1>
            <p className="text-muted-foreground mt-2">Suivez vos revenus et paiements</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefreshData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={handleExportPayments} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Statistiques des revenus */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-primary-700/50 border-primary-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-300 mb-2">Revenus du mois</p>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(stats.revenus_mois)}
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary-700/50 border-primary-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-300 mb-2">Revenus total</p>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(stats.revenus_total)}
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <Wallet className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary-700/50 border-primary-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-300 mb-2">Solde actuel</p>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(stats.balance)}
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-full">
                  <Banknote className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary-700/50 border-primary-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-300 mb-2">Objectif mensuel</p>
                  <p className="text-3xl font-bold text-white">
                    {Math.round(stats.pourcentage_objectif)}%
                  </p>
                </div>
                <div className="p-3 bg-orange-500/20 rounded-full">
                  <TrendingUp className="w-6 h-6 text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Graphique des revenus mensuels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-white">Évolution des revenus</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E3B23C" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#E3B23C" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#A3B3C6" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#A3B3C6" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value/1000000}M`} />
                    <Tooltip contentStyle={{backgroundColor: '#0A4132', border: '1px solid #E3B23C', color: '#FFFFFF'}}/>
                    <Area type="monotone" dataKey="revenue" stroke="#E3B23C" fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="text-white">Nombre de missions par mois</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <XAxis dataKey="month" stroke="#A3B3C6" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#A3B3C6" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{backgroundColor: '#0A4132', border: '1px solid #E3B23C', color: '#FFFFFF'}}/>
                    <Bar dataKey="count" fill="#E3B23C" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtres pour les paiements */}
        <Card className="mb-6 bg-card/50 border-border">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher un paiement..."
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="SUCCESS">Réussi</SelectItem>
                  <SelectItem value="FAILED">Échoué</SelectItem>
                  <SelectItem value="REFUNDED">Remboursé</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="date"
                placeholder="Date de début"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full sm:w-48"
              />
              <Input
                type="date"
                placeholder="Date de fin"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full sm:w-48"
              />
            </div>
          </CardContent>
        </Card>

        {/* Liste des paiements */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Chargement des paiements...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-destructive">Erreur: {error}</div>
          </div>
        ) : payments.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="p-12 text-center">
              <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Aucun paiement trouvé</h3>
              <p className="text-muted-foreground">Vous n'avez pas encore de paiements enregistrés.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {payments.map((payment) => (
              <Card key={payment.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {getPaymentMethodIcon(payment.payment_method)}
                        <h3 className="text-lg font-semibold">Paiement #{payment.id.slice(-8)}</h3>
                        {getStatusBadge(payment.status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>Date: {formatDate(payment.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          <span>Méthode: {payment.payment_method}</span>
                        </div>
                        {payment.booking && (
                          <>
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4" />
                              <span>Mission: #{payment.booking.id.slice(-8)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>Trajet: {payment.booking.request?.from_location?.name} → {payment.booking.request?.to_location?.name}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">
                          {formatCurrency(payment.amount)}
                        </div>
                        <div className="text-sm text-muted-foreground">Montant</div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default TransporterEarnings;
